import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AtSign, Copy, Check, Pencil, Search } from "lucide-react";

export const addressOf = (handle) => (handle ? `${handle}@velluto` : "");
export const stripTag = (v) => v.trim().toLowerCase().replace(/^@/, "").replace(/@velluto$/, "");

// Inline "handle@velluto" tag shown next to names.
export function VellutoAddress({ user, className = "", testId }) {
  if (!user?.handle) return null;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 text-text-muted font-mono text-xs ${className}`}>
      <AtSign size={11} className="text-gold" />{addressOf(user.handle)}
    </span>
  );
}

// Own address with copy + inline rename (PUT /creator/handle).
export function AddressEditor({ handle, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(handle || "");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(addressOf(handle)); } catch { /* ignore */ }
    setCopied(true); toast.success("Address copied"); setTimeout(() => setCopied(false), 1500);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/creator/handle", { handle: stripTag(val) });
      onChanged?.(data.handle); setEditing(false); toast.success(`You're now ${data.address}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update address"); } finally { setSaving(false); }
  };
  if (editing) {
    return (
      <div data-testid="address-editor" className="flex items-center gap-2 rounded-2xl border border-gold/40 bg-black/30 px-4 py-3">
        <AtSign size={14} className="text-gold shrink-0" />
        <input data-testid="address-input" value={val} onChange={(e) => setVal(e.target.value)} maxLength={24} autoFocus
          className="flex-1 min-w-0 bg-transparent outline-none font-mono text-sm" />
        <span className="text-xs text-text-muted font-mono">@velluto</span>
        <button data-testid="address-save" disabled={saving} onClick={save} className="text-[10px] uppercase tracking-[0.15em] text-gold disabled:opacity-40">{saving ? "…" : "Save"}</button>
        <button onClick={() => { setEditing(false); setVal(handle || ""); }} className="text-[10px] uppercase tracking-[0.15em] text-text-muted">Cancel</button>
      </div>
    );
  }
  return (
    <div data-testid="address-display" className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <AtSign size={14} className="text-gold shrink-0" />
      <p data-testid="address-value" className="flex-1 min-w-0 truncate font-mono text-sm">{addressOf(handle)}</p>
      <button data-testid="address-edit" onClick={() => setEditing(true)} title="Customise" className="p-1.5 text-text-muted hover:text-gold"><Pencil size={13} /></button>
      <button data-testid="address-copy" onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/40 text-gold text-[10px] uppercase tracking-[0.15em] hover:bg-gold/10">
        {copied ? <Check size={11} /> : <Copy size={11} />} Copy
      </button>
    </div>
  );
}

// "Find a creator" box — type ava.rose@velluto (or a handle) to jump to the profile.
export function FindByAddress({ className = "" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    const h = stripTag(q);
    if (!h) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/c/${encodeURIComponent(h)}`);
      nav(`/profile/${data.id}`);
    } catch { toast.error(`No member found at ${h}@velluto`); } finally { setBusy(false); }
  };
  return (
    <form data-testid="find-address-form" onSubmit={go} className={`flex items-center gap-2 rounded-full border border-white/15 bg-black/20 pl-4 pr-1.5 py-1.5 focus-within:border-gold/50 transition-colors ${className}`}>
      <AtSign size={14} className="text-gold shrink-0" />
      <input data-testid="find-address-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="name@velluto"
        className="flex-1 min-w-0 bg-transparent outline-none text-sm font-mono placeholder:text-text-muted/60" />
      <button data-testid="find-address-submit" disabled={busy || !q.trim()} className="p-2 rounded-full bg-gold text-ink disabled:opacity-40"><Search size={13} /></button>
    </form>
  );
}
